import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@/components/Icon";
import { Screen } from "@/components/Layout";
import { Spinner } from "@/components/Controls";
import EmberOrb from "@/components/EmberOrb";
import { colors, spacing, radius, fonts, type } from "@/theme";
import { getStoredUserId, fetchUser, fetchCheckin, fetchNudges, fetchStats, fetchVoiceSettings, setCompanionMode, matchMood } from "@/api";
import { logError } from "@/lib/log";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function Home() {
  const navigate = useNavigate();
  const [name, setName] = useState(null);
  const [checkin, setCheckin] = useState(null);
  const [stats, setStats] = useState(null);
  const [voiceMood, setVoiceMood] = useState(null);
  const [persona, setPersona] = useState(null);
  const [companionMode, setCompanionModeState] = useState("stranger");
  const [userId, setUserId] = useState(null);
  const [morningHello, setMorningHello] = useState(null);

  useEffect(() => {
    (async () => {
      const id = getStoredUserId();
      if (!id) {
        navigate("/onboarding", { replace: true });
        return;
      }
      setUserId(id);
      try {
        const data = await fetchUser(id);
        setName(data.user.first_name);
        setPersona(data.active_persona ? {
          name: data.active_persona.name,
          relationship: data.active_persona.relationship,
          mode_locked: data.active_persona.mode_locked,
        } : null);
        setCompanionModeState(data.companion_mode ?? "stranger");
        if (data.is_returning && data.memories.length > 0) fetchCheckin(id).then(setCheckin);
        fetchStats(id).then(setStats);
        fetchVoiceSettings().then((v) => {
          if (v?.elevenlabs_enabled) {
            const m = matchMood(v);
            setVoiceMood(m ? { label: m.label, icon: m.icon } : { label: "Custom", icon: "sliders" });
          }
        });
        const isMorning = new Date().getHours() < 12;
        if (isMorning) {
          const lines = await fetchNudges(id);
          if (lines.length) {
            const todayKey = new Date().toISOString().slice(0, 10);
            if (localStorage.getItem("ember_hello_date") !== todayKey) {
              setMorningHello(lines[Math.floor(Math.random() * lines.length)]);
              localStorage.setItem("ember_hello_date", todayKey);
            }
          }
        }
      } catch (e) {
        logError("Home load", e);
        setName("friend");
      }
    })();
  }, [navigate]);

  const startTalk = (opener) => navigate("/conversation", opener ? { state: { opener } } : undefined);

  const toggleCompanionMode = async () => {
    if (!userId || !persona || persona.mode_locked) return;
    const next = companionMode === "familiar" ? "stranger" : "familiar";
    setCompanionModeState(next);
    const res = await setCompanionMode(userId, next === "familiar");
    if (res) setCompanionModeState(res.familiar ? "familiar" : "stranger");
  };

  if (!name) {
    return <Screen><div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}><Spinner size={40} /></div></Screen>;
  }

  return (
    <Screen testID="home-screen" style={{ display: "flex", flexDirection: "column", padding: `${spacing.lg}px ${spacing.xl}px 0` }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <h1 data-testid="home-greeting" style={{ ...fonts.displayBold, fontSize: type["3xl"], lineHeight: 1.2, color: colors.onSurface, margin: 0 }}>
          {greeting()},<br />{name}
        </h1>
        <button data-testid="home-settings-button" onClick={() => navigate("/settings")} className="ember-press"
          style={{ width: 52, height: 52, borderRadius: 26, background: colors.surfaceSecondary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name="settings" size={26} color={colors.brand} />
        </button>
      </div>

      <div style={{ flex: 1, paddingBottom: spacing.xl }}>
        {morningHello && (
          <div data-testid="daily-hello-card" style={{ display: "flex", alignItems: "flex-start", gap: spacing.md, background: colors.brandSecondary, borderRadius: radius.lg, padding: spacing.lg, marginTop: spacing.lg }}>
            <div style={{ width: 44, height: 44, borderRadius: 22, background: colors.brandPrimary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="sun" size={22} color={colors.onBrandPrimary} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ ...fonts.textSemiBold, fontSize: type.base, lineHeight: 1.45, color: colors.onBrandPrimary }}>{morningHello}</div>
              <button data-testid="daily-hello-talk" onClick={() => startTalk(morningHello)} className="ember-press"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: colors.brandPrimary, borderRadius: radius.pill, padding: `${spacing.sm}px ${spacing.lg}px`, marginTop: spacing.md }}>
                <span style={{ ...fonts.textExtraBold, fontSize: type.base, color: colors.onBrandPrimary }}>Let&rsquo;s talk</span>
                <Icon name="arrow-right" size={16} color={colors.onBrandPrimary} />
              </button>
            </div>
            <button data-testid="daily-hello-dismiss" onClick={() => setMorningHello(null)} style={{ padding: 2 }}>
              <Icon name="x" size={18} color={colors.onBrandPrimary} />
            </button>
          </div>
        )}

        {persona ? (
          <button data-testid="home-persona-label" onClick={persona.mode_locked ? () => navigate("/settings") : toggleCompanionMode} style={pill}>
            <Icon name={companionMode === "familiar" ? "user" : "message-circle"} size={18} color={colors.onBrandTertiary} />
            <span style={{ ...fonts.textBold, fontSize: type.base, color: colors.onBrandTertiary }}>
              {companionMode === "familiar" ? `Voice of ${persona.name}` : "Everyday companion"}
            </span>
            {!persona.mode_locked && (
              <>
                <Icon name="refresh-cw" size={15} color={colors.onBrandTertiary} />
                <span style={{ ...fonts.textSemiBold, fontSize: type.base, color: colors.onBrandTertiary, opacity: 0.8 }}>
                  {companionMode === "familiar" ? "Tap to switch" : `Switch to ${persona.name}`}
                </span>
              </>
            )}
          </button>
        ) : (
          voiceMood && (
            <button data-testid="home-voice-label" onClick={() => navigate("/settings")} style={pill}>
              <Icon name={voiceMood.icon} size={18} color={colors.brand} />
              <span style={{ ...fonts.textBold, fontSize: type.base, color: colors.onBrandTertiary }}>Voice · {voiceMood.label}</span>
            </button>
          )
        )}

        {stats && (stats.streak >= 2 || stats.this_week >= 1) && (
          <div data-testid="streak-pill" style={{ ...pill, background: colors.surfaceSecondary }}>
            <Icon name={stats.streak >= 2 ? "award" : "message-circle"} size={20} color={colors.brand} />
            <span style={{ ...fonts.textBold, fontSize: type.base, color: colors.onSurface }}>
              {stats.streak >= 2 ? `${stats.streak} days in a row — wonderful!` : `${stats.this_week} ${stats.this_week === 1 ? "chat" : "chats"} this week`}
            </span>
          </div>
        )}

        {checkin && (
          <button data-testid="checkin-card" onClick={() => startTalk(checkin)} className="ember-press"
            style={{ display: "flex", alignItems: "center", gap: spacing.md, width: "100%", textAlign: "left", background: colors.brandTertiary, borderRadius: radius.lg, padding: spacing.lg, marginTop: spacing.lg }}>
            <div style={{ width: 44, height: 44, borderRadius: 22, background: colors.brandPrimary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="heart" size={22} color={colors.onBrandPrimary} />
            </div>
            <span style={{ flex: 1, ...fonts.textSemiBold, fontSize: type.base, lineHeight: 1.45, color: colors.onBrandTertiary }}>{checkin}</span>
          </button>
        )}

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: `${spacing["3xl"]}px 0` }}>
          <div className="ember-orb-in" style={{ marginBottom: spacing["3xl"] }}><EmberOrb size={150} /></div>
          <button data-testid="tap-to-talk-button" onClick={() => startTalk()}
            style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", background: "none" }}>
            <div className="ember-pulse" style={{ position: "absolute", width: 240, height: 240, borderRadius: 120, background: colors.brandTertiary }} />
            <div className="ember-press" style={{ position: "relative", width: 210, height: 210, borderRadius: 105, background: colors.brandPrimary, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: spacing.sm }}>
              <Icon name="mic" size={56} color={colors.onBrandPrimary} />
              <span style={{ ...fonts.textExtraBold, fontSize: type.xl, color: colors.onBrandPrimary }}>Tap to talk</span>
            </div>
          </button>
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: spacing.md, flexWrap: "wrap", paddingBottom: spacing.xl }}>
          <div data-testid="pill-remembers" style={infoPill}><Icon name="heart" size={22} color={colors.brand} /><span style={pillText}>Remembers you</span></div>
          <div data-testid="pill-always" style={infoPill}><Icon name="clock" size={22} color={colors.brand} /><span style={pillText}>Always here</span></div>
        </div>
      </div>
    </Screen>
  );
}

const pill = { display: "inline-flex", alignItems: "center", gap: spacing.sm, background: colors.brandTertiary, borderRadius: radius.pill, padding: `${spacing.sm}px ${spacing.lg}px`, marginTop: spacing.lg };
const infoPill = { display: "flex", alignItems: "center", gap: spacing.sm, background: colors.surfaceSecondary, borderRadius: radius.pill, border: `2px solid ${colors.border}`, padding: `${spacing.md}px ${spacing.lg}px`, minHeight: 56 };
const pillText = { ...fonts.textBold, fontSize: type.base, color: colors.onSurface };
